// Scripted-flow bodies for PROXY Ace intents — mirrors AcePage.tsx's existing
// canned-flow pattern (runChase/presentExceptionCard: compute from local
// data, append via addAgentMsg, never touch the real LLM). Kept out of
// AcePage.tsx to minimize that file's diff/regression surface.
import type { ProxyJob } from '../data/mockProxyJobs';
import { PROXY_SEED_TODAY, proxyNowStamp } from '../data/mockProxyJobs';
import { resolveProxyJob } from '../utils/proxyQueue';
import { calculateRdq } from '../utils/rdqCalculator';
import { resolveOutreachContact } from '../data/proxyFallbackSolicitors';
import type { ProxyIntentMatch } from './proxyAceIntents';

export interface ProxyAceCtx {
  jobs: ProxyJob[];
  appendFollowUp: (jobId: string, entry: { timestamp: string; author: string; type: string; recipientEmail: string; subject: string; bodyPreview: string; jobIds: string[] }) => void;
  addAgentMsg: (content: string, actions?: { label: string; fn: () => void }[]) => void;
  navigate: (path: string, opts?: any) => void;
  showToast?: (msg: string) => void;
  // Opens the RDQ_ORDER ACE workflow panel for a single job (Proxy Intake
  // Queue's registered workflow) — replaces the old /proxy/rdq calculator route.
  openRdqOrderTask: (job: ProxyJob) => void;
}

function runCollectionsSummary(ctx: ProxyAceCtx) {
  const resolved = ctx.jobs.map(j => resolveProxyJob(j, null)).filter(j => j.effectiveStatus === 'ACTIVE');
  const counts = { Red: 0, Yellow: 0, Green: 0 } as Record<string, number>;
  resolved.forEach(j => { counts[j.band]++; });
  const topRed = resolved.filter(j => j.band === 'Red').sort((a, b) => a.daysToVoteCutoff - b.daysToVoteCutoff).slice(0, 5);
  const rows = topRed.map(j => `<div>${j.jobId} — ${j.issuer} (${j.daysToVoteCutoff}d, ${j.decision.action})</div>`).join('');
  ctx.addAgentMsg(
    `Collections summary as of ${PROXY_SEED_TODAY}: <strong>${counts.Red} Red</strong>, <strong>${counts.Yellow} Yellow</strong>, <strong>${counts.Green} Green</strong>.${rows ? `<br><br>Nearest to cutoff:<br>${rows}` : ''}`,
    // The Follow-Up Queue workflow isn't registered yet — land on the Proxy
    // Intake Queue until it is.
    [{ label: 'Open Proxy Intake Queue', fn: () => ctx.navigate('/proxy') }],
  );
}

function runDraftRedFollowUps(ctx: ProxyAceCtx, solicitorFilter?: string) {
  const resolved = ctx.jobs.map(j => resolveProxyJob(j, null)).filter(j => j.band === 'Red' && j.effectiveStatus === 'ACTIVE');
  const matching = solicitorFilter ? resolved.filter(j => (j.solicitor || '').toLowerCase().includes(solicitorFilter.toLowerCase())) : resolved;
  // No contact on file → contact tracing first (Exceptions), not a draft.
  const filtered = matching.filter(j => resolveOutreachContact(j));
  const noContact = matching.length - filtered.length;
  if (filtered.length === 0) {
    ctx.addAgentMsg(`No active Red jobs found${solicitorFilter ? ` for "${solicitorFilter}"` : ''}.`);
    return;
  }
  const shown = filtered.slice(0, 8);
  const rows = shown.map(j => `<div style="margin-bottom:4px">${j.jobId} — ${j.issuer} (${j.daysToVoteCutoff}d)</div>`).join('');
  ctx.addAgentMsg(
    `Drafted red follow-ups${solicitorFilter ? ` for ${solicitorFilter}` : ''} — ${filtered.length} job(s):<br>${rows}${noContact ? `<br>${noContact} more job(s) have no contact on file — see Exceptions for contact tracing.` : ''}`,
    shown.map(j => ({
      label: `Send — ${j.jobId}`,
      fn: () => {
        ctx.appendFollowUp(j.jobId, {
          timestamp: proxyNowStamp(), author: 'ICT Associate', type: 'red_followup',
          recipientEmail: resolveOutreachContact(j)!.email,
          subject: `Proxy Material Follow-Up — ${j.issuer} (Job ${j.jobId})`,
          bodyPreview: 'Requesting status on outstanding proxy material.', jobIds: [j.jobId],
        });
        ctx.showToast?.(`Follow-up sent for ${j.jobId}`);
      },
    })),
  );
}

function runRdqNeeded(ctx: ProxyAceCtx) {
  const resolved = ctx.jobs.map(j => resolveProxyJob(j, null)).filter(j => j.effectiveStatus === 'ACTIVE' && j.materialStatus === 'Partial');
  if (!resolved.length) { ctx.addAgentMsg('No jobs currently flagged as needing an RDQ recalculation.'); return; }
  const shown = resolved.slice(0, 8);
  const rows = shown.map(j => `<div>${j.jobId} — ${j.issuer}</div>`).join('');
  ctx.addAgentMsg(
    `${resolved.length} job(s) may need an RDQ recalculation (partial material on file):<br>${rows}`,
    shown.map(j => ({ label: `Recalculate — ${j.jobId}`, fn: () => ctx.openRdqOrderTask(j) })),
  );
}

function runCutoffThisWeek(ctx: ProxyAceCtx) {
  const resolved = ctx.jobs.map(j => resolveProxyJob(j, null)).filter(j => j.effectiveStatus === 'ACTIVE' && j.daysToVoteCutoff >= 0 && j.daysToVoteCutoff <= 7);
  if (!resolved.length) { ctx.addAgentMsg('No jobs hit their vote cutoff this week.'); return; }
  resolved.sort((a, b) => a.daysToVoteCutoff - b.daysToVoteCutoff);
  const rows = resolved.map(j => `<div>${j.jobId} — ${j.issuer} (${j.daysToVoteCutoff}d, ${j.decision.action})</div>`).join('');
  ctx.addAgentMsg(`${resolved.length} job(s) hit their vote cutoff this week:<br>${rows}`, [
    { label: 'Open Proxy Intake Queue', fn: () => ctx.navigate('/proxy') },
  ]);
}

function runRdqCalculateForJob(ctx: ProxyAceCtx, jobId: string) {
  const job = ctx.jobs.find(j => j.jobId === jobId);
  if (!job) { ctx.addAgentMsg(`I couldn't find job ${jobId}.`); return; }
  const result = calculateRdq({ country: job.country, noElec: false, mailingAsNaa: job.country === 'CA', activeFullSets: job.fullSetsNeeded, activeNaa: job.naaNeeded, activeElectronic: 0 });
  const lines = result.lines.map(l => `${l.materialType}: ${l.qtyToOrder}`).join(', ');
  ctx.addAgentMsg(`RDQ for job ${jobId} (${job.issuer}): ${lines}. Total ${result.totalSets}.`, [
    { label: 'Open RDQ Order workflow', fn: () => ctx.openRdqOrderTask(job) },
  ]);
}

function runJobShortage(ctx: ProxyAceCtx, jobId: string) {
  const job = ctx.jobs.find(j => j.jobId === jobId);
  if (!job) { ctx.addAgentMsg(`I couldn't find job ${jobId}.`); return; }
  if (!job.shortageLines || !job.shortageLines.length) { ctx.addAgentMsg(`No shortage on record for job ${jobId} (${job.issuer}).`); return; }
  const rows = job.shortageLines.map(l => `<div>${l.materialType}: ordered ${l.ordered}, received ${l.received}, short ${l.shortage}</div>`).join('');
  ctx.addAgentMsg(`Job ${jobId} (${job.issuer}) shortage:<br>${rows}`, [
    { label: 'Open Proxy Intake Queue', fn: () => ctx.navigate('/proxy') },
  ]);
}

function runContestsWaitingSignature(ctx: ProxyAceCtx, days: number) {
  const stale = ctx.jobs.filter(j => {
    if (!j.contest || j.contest.formStatus !== 'SENT') return false;
    const last = j.followUpHistory[j.followUpHistory.length - 1];
    if (!last) return false;
    const diffDays = (new Date(PROXY_SEED_TODAY).getTime() - new Date(last.timestamp.slice(0, 10)).getTime()) / (1000 * 60 * 60 * 24);
    return diffDays >= days;
  });
  if (!stale.length) { ctx.addAgentMsg(`No contests waiting on signature past ${days} days.`); return; }
  const rows = stale.map(j => `<div>${j.jobId} — ${j.issuer} (${j.contest!.attempts}/3 attempts)</div>`).join('');
  ctx.addAgentMsg(`${stale.length} contest(s) waiting on signature past ${days} days:<br>${rows}`, [
    // The Contest workflow isn't registered yet — land on the Proxy Intake
    // Queue until it is.
    { label: 'Open Proxy Intake Queue', fn: () => ctx.navigate('/proxy') },
  ]);
}

export function runProxyIntent(intent: ProxyIntentMatch, ctx: ProxyAceCtx): void {
  switch (intent.handler) {
    case 'collectionsSummary': return runCollectionsSummary(ctx);
    case 'draftRedFollowUps': return runDraftRedFollowUps(ctx, intent.params.solicitor);
    case 'rdqNeeded': return runRdqNeeded(ctx);
    case 'cutoffThisWeek': return runCutoffThisWeek(ctx);
    case 'rdqCalculateForJob': return runRdqCalculateForJob(ctx, intent.params.jobId);
    case 'jobShortage': return runJobShortage(ctx, intent.params.jobId);
    case 'contestsWaitingSignature': return runContestsWaitingSignature(ctx, parseInt(intent.params.days, 10));
  }
}
